import { TourStep } from '../../constants/tour';

export const MOBILE_BREAKPOINT = 768;

export const isMobileDevice = (): boolean => window.innerWidth < MOBILE_BREAKPOINT;

export interface HighlightRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const getEffectiveStepProps = (step: TourStep) => {
  const isMobile = isMobileDevice();
  return {
    target: (isMobile && step.mobileTarget) ? step.mobileTarget : step.target as string,
    title: (isMobile && step.mobileTitle) ? step.mobileTitle : step.title,
    content: (isMobile && step.mobileContent) ? step.mobileContent : step.content,
    placement: (isMobile && step.mobilePlacement) ? step.mobilePlacement : step.placement,
    shouldSkip: (isMobile && step.hideOnMobile) || (!isMobile && step.hideOnDesktop),
    requiresClick: isMobile && step.mobileRequiresClick !== undefined
      ? step.mobileRequiresClick : step.requiresClick,
    clickPrompt: (isMobile && step.mobileClickPrompt) ? step.mobileClickPrompt : step.clickPrompt,
  };
};

export const waitForElement = (selector: string, timeout = 5000): Promise<Element | null> => {
  return new Promise(resolve => {
    const el = document.querySelector(selector);
    if (el) return resolve(el);

    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) { observer.disconnect(); resolve(el); }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    setTimeout(() => { observer.disconnect(); resolve(null); }, timeout);
  });
};

export const getTargetRect = (selector: string, padding = 8): HighlightRect | null => {
  const el = document.querySelector(selector);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  return {
    x: rect.x - padding,
    y: rect.y - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
  };
};

export const getMultiTargetRect = (selector: string, padding = 8): HighlightRect | null => {
  const elements = document.querySelectorAll(selector);
  if (elements.length === 0) return null;

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  elements.forEach(el => {
    const rect = el.getBoundingClientRect();
    minX = Math.min(minX, rect.x);
    minY = Math.min(minY, rect.y);
    maxX = Math.max(maxX, rect.x + rect.width);
    maxY = Math.max(maxY, rect.y + rect.height);
  });

  return {
    x: minX - padding,
    y: minY - padding,
    width: (maxX - minX) + padding * 2,
    height: (maxY - minY) + padding * 2,
  };
};

/**
 * Scrolls an element into view on mobile devices.
 * Positions element in the upper third of the viewport to leave room for tooltip below.
 */
export const scrollElementIntoViewMobile = (selector: string): void => {
  if (!isMobileDevice()) return;

  const el = document.querySelector(selector);
  if (!el) return;

  const rect = el.getBoundingClientRect();
  const viewportHeight = window.innerHeight;

  // Reserve space for tooltip (approximately 200px for tooltip + padding)
  const tooltipSpace = 200;
  const safeZoneTop = 80; // Space from top for header
  const safeZoneBottom = viewportHeight - tooltipSpace;

  // Check if element is outside the safe zone
  const isAboveSafeZone = rect.top < safeZoneTop;
  const isBelowSafeZone = rect.bottom > safeZoneBottom;

  if (isAboveSafeZone || isBelowSafeZone) {
    // Scroll element to upper portion of viewport to leave room for tooltip
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // Additional scroll adjustment to account for fixed headers
    setTimeout(() => {
      window.scrollBy({ top: -100, behavior: 'smooth' });
    }, 100);
  }
};
